import CheckIcon from '@mui/icons-material/Check'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { Chip, ClickAwayListener, IconButton, TextField, Tooltip } from '@mui/material'
import { Box } from '@mui/system'
import React, { useEffect, useRef, useState } from 'react'
import { useAppDispatch } from '../../../Store/Hooks'
import { showAlert } from '../../../Store/Slice/alert.slice'
import { copyToClipboard } from '../../../Helpers/Util'
const Tags = ({ data, handleDelete }: any) => {
  return <Chip label={data} onDelete={() => handleDelete(data)} />
}

export interface MUIInputTagsProps {
  /** name: unique fieldName */
  name: string

  /** type: input type of the field, default is TEXT*/
  type: string
  /**
   * error: Error message for the field.
   */
  error: any
  /**
   * User facing label of the field.
   */
  label: string
  /**
   * callback function to be triggered on onChange event of input field.
   * @returns
   */
  onChange: (tags: string[]) => void
  required: boolean
  placeholder: string
  /**
   * List of tags to be shown, if any
   */
  value: string[]
  disabled?: boolean
  enableCopyPaste?: boolean
}

/**
 * My component renders a text message with an optional click handler.
 */
export const MUIInputTags: React.FC<MUIInputTagsProps> = ({
  name,
  type = 'TEXT',
  error,
  label,
  onChange,
  required,
  placeholder,
  value,
  disabled,
  enableCopyPaste = false,
}) => {
  const [tags, setTags] = useState<any>([])
  const [inputValue, setInputValue] = useState<any>('')
  const [focus, setFocus] = useState<any>(false)
  const [copied, setCopied] = useState(false)
  const tagRef: any = useRef<any>()

  const dispatch = useAppDispatch()

  const handleDelete = (value: any) => {
    const newtags = tags.filter((val: any) => val !== value)
    setTags(newtags)
    onChange(newtags)
  }
  const handleOnSubmit = (e: any) => {
    if (inputValue?.trim()) {
      if (e?.code === 'Enter' || e?.code === 'Comma') {
        e?.preventDefault?.()
        if (tags.includes(inputValue)) {
          dispatch(showAlert([true, 'this ' + label + ' already present', 'error']))
          setInputValue('')
          return
        }
        onChange([...tags, inputValue])
        setInputValue('')
      }
    } else {
      setInputValue('')
    }
  }

  /**
   * Handles the `onPaste` event for an HTMLInputElement.
   *
   * This function parses the pasted text, extracts tags,
   * and updates the state with the combined and unique tags.
   * @param e - The React `ClipboardEvent` object triggered by the `onPaste` event.
   */
  const handleOnPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    // Prevent default behaviour to prevent handleInputChange from triggering.
    e.preventDefault()
    const pastedData: string = e.clipboardData.getData('Text') ?? ''

    // Split pasted text into individual tags, by comma(,).
    const pastedTags: string[] = pastedData
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
    const merged = [...new Set([...tags, ...pastedTags])]
    setTags(merged)
    onChange(merged)
    setInputValue('')
  }

  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!tags.length) return
    copyToClipboard(tags.join(', '), () => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }

  const handleInputChange = (e: any) => {
    const input = e.target
    const valueWithoutComma = input.value.replaceAll(',', '') // Remove commas from the input value
    input.value = valueWithoutComma
    setInputValue(input.value)
  }

  const getPlaceholder = () => {
    if (value?.length) {
      return ''
    }
    if (tags?.length) {
      return ''
    }
    return placeholder ?? label
  }

  useEffect(() => {
    if (value?.length) setTags(value)
  }, [value])

  return (
    <div className='tag__input '>
      <ClickAwayListener onClickAway={() => setFocus(false)}>
        <Box sx={{ position: 'relative' }}>
          <TextField
            data-testid='mui-input-tag'
            inputRef={tagRef}
            name={name}
            type={type}
            error={!!error?.[name]}
            onKeyDown={handleOnSubmit}
            fullWidth
            disabled={disabled}
            variant='outlined'
            multiline={true}
            inputProps={{ id: name, maxLength: 100 }}
            onFocus={() => setFocus(true)}
            onPaste={handleOnPaste}
            onBlur={() => {
              handleOnSubmit({ code: 'Enter' })
            }}
            maxRows={1}
            helperText={error?.[name]?.message as string}
            margin='none'
            value={inputValue}
            onChange={handleInputChange}
            placeholder={getPlaceholder()}
            label={
              <span>
                {label}
                {required && <span style={{ color: '#D4351C' }}>*</span>}
              </span>
            }
            InputProps={{
              name: name,
              sx: enableCopyPaste && tags.length > 0 ? { paddingRight: '36px' } : {},
              startAdornment: (
                <div className='d-flex justify-content-between w-100'>
                  <Box
                    sx={{
                      margin: '0 0.2rem 0 0',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItemsCenter: 'center',
                      padding: value?.length ? '8px 8px 0 8px' : 0,
                    }}
                    className={'width-fill-available'}
                  >
                    <div className='d-flex justify-content-start flex-wrap width-fill-available gap-2'>
                      {(focus || tags.length <= 2 ? tags : tags.slice(0, 2)).map((data: any) => (
                        <Tags data={data} handleDelete={handleDelete} key={data} />
                      ))}
                      {!focus && tags.length > 2 && (
                        <div className='ml-1 d-flex flex-no-wrap justify-content-center align-items-center'>
                          +{tags.length - 2} others
                        </div>
                      )}
                    </div>
                  </Box>
                  <div className='position-relative top-5 width-5'>
                    {enableCopyPaste && tags.length > 0 && (
                      <Tooltip title={copied ? 'Copied!' : 'Copy selected'}>
                        <IconButton
                          size='small'
                          onClick={handleCopy}
                          onMouseDown={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                          }}
                          sx={{ position: 'absolute', top: 4, right: 4, zIndex: 1, p: '2px' }}
                        >
                          {copied ? (
                            <CheckIcon fontSize='small' />
                          ) : (
                            <ContentCopyIcon fontSize='small' />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                </div>
              ),
            }}
            InputLabelProps={{
              shrink: !!tags.length || !!inputValue || focus,
            }}
          />
        </Box>
      </ClickAwayListener>
    </div>
  )
}
